import { useEffect, useRef, useState } from 'react';
import { Reorder } from 'motion/react';
import { SlidersHorizontal, Check, GripVertical, Lock } from 'lucide-react';
import styles from './ColumnPicker.module.css';

export interface ColumnOption {
  id: string;
  label: string;
  /** Always shown, cannot be hidden or moved (rank, title). */
  locked?: boolean;
}

export interface ColumnPickerProps {
  /** All selectable columns, in current display order. */
  columns: ColumnOption[];
  /** Ids currently visible. */
  visible: string[];
  onChange: (visible: string[]) => void;
  /** Reordered ids, locked columns first. */
  onReorder: (order: string[]) => void;
  onReset: () => void;
}

/**
 * Choose which columns appear and in what order — matches the licensing
 * ColumnsMenu: a pill trigger consistent with the Select filters, locked
 * columns pinned on top, and the movable columns drag-to-reorder (motion
 * Reorder) with a grip handle. Checkbox toggles visibility.
 */
export function ColumnPicker({ columns, visible, onChange, onReorder, onReset }: ColumnPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const locked = columns.filter((c) => c.locked);
  const movable = columns.filter((c) => !c.locked);
  const movableOrder = movable.map((c) => c.id);
  const byId = new Map(columns.map((c) => [c.id, c]));

  const toggle = (id: string) => {
    onChange(visible.includes(id) ? visible.filter((v) => v !== id) : [...visible, id]);
  };

  /* Reorder.Group hands back the new order of movable ids; re-prepend the locked
     ids so the parent gets a complete order (locked always first). */
  const handleReorder = (nextMovable: string[]) => {
    onReorder([...locked.map((c) => c.id), ...nextMovable]);
  };

  const shownCount = movable.filter((c) => visible.includes(c.id)).length;

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type={'button'}
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={'Choose columns'}
      >
        <SlidersHorizontal size={14} strokeWidth={2} />
        {'Columns'}
        <span className={styles.count}>
          {shownCount}/{movable.length}
        </span>
      </button>

      {open && (
        <div className={styles.menu}>
          <div className={styles.head}>
            <span className={styles.title}>{'Columns'}</span>
            <button type={'button'} className={styles.reset} onClick={onReset}>
              {'Reset'}
            </button>
          </div>

          {/* Locked columns first — always on, not draggable. */}
          {locked.map((c) => (
            <div key={c.id} className={`${styles.row} ${styles.rowLocked}`}>
              <span className={`${styles.box} ${styles.boxOn}`}>
                <Check size={11} strokeWidth={3} />
              </span>
              <span className={styles.label}>{c.label}</span>
              <Lock size={12} className={styles.lockIcon} />
            </div>
          ))}

          {/* Movable columns — drag to reorder. */}
          <Reorder.Group
            axis={'y'}
            values={movableOrder}
            onReorder={handleReorder}
            className={styles.reorderGroup}
          >
            {movableOrder.map((id) => {
              const col = byId.get(id);
              if (!col) return null;
              const isVisible = visible.includes(id);
              return (
                <Reorder.Item key={id} value={id} className={styles.row}>
                  <button
                    type={'button'}
                    className={styles.toggle}
                    onClick={() => toggle(id)}
                    aria-pressed={isVisible}
                  >
                    <span className={`${styles.box} ${isVisible ? styles.boxOn : ''}`}>
                      {isVisible && <Check size={11} strokeWidth={3} />}
                    </span>
                    <span className={styles.label}>{col.label}</span>
                  </button>
                  <GripVertical size={14} className={styles.grip} aria-label={'Drag to reorder'} />
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
        </div>
      )}
    </div>
  );
}
